type ICampaignQueryParams = Record<string | number, string | number>;

export const dateFormatter = (
    dateItem: string | Date,
    format?: `year` | `month` | `day`
) => {
    if (!dateItem || (typeof dateItem === `string` && dateItem.length === 0)) {
        return;
    }
    if (typeof dateItem === `string`) {
        dateItem = new Date(dateItem);
    }
    if (isNaN(dateItem.getTime())) {
        console.error(`Invalid date provided to dateFormatter:`, dateItem);
        return;
    }

    const year = dateItem.getFullYear();
    const month = String(dateItem.getMonth() + 1).padStart(2, `0`);
    const day = String(dateItem.getDate()).padStart(2, `0`);
    let formattedDate = ``;

    if (format === `year`) {
        formattedDate = `${year}-${month}-${day}`;
    } else if (format === `day`) {
        formattedDate = `${day}-${month}-${year}`;
    } else {
        formattedDate = `${month}-${day}-${year}`;
    }

    return formattedDate;
};

//filter array of objects by key name
export const filterArrayOfObjByKey = (arr: any[], passedKey: string | number) =>
    arr?.filter((item) => passedKey in item);

export const isBrowser = typeof window !== `undefined`;

export const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    };
};

export const ObjectComparisonBool = <
    T extends Record<string | number, string | number>
>(
    firstObj: T,
    secondObj: T
) => {
    const firstObjectKeys = Object.keys(firstObj);
    const secondUTMSKeys = Object.keys(secondObj);

    return firstObjectKeys.every((key) => secondUTMSKeys.includes(key));
};

export const setLocalStorage = (
    localStorageName: string,
    localStorageKey: number | string,
    localStorageVal: ICampaignQueryParams | ICampaignQueryParams[]
) => {
    const localStorageObject = {
        [localStorageKey]: localStorageVal,
    };
    let localObjString = ``;

    if (isBrowser) {
        localObjString = JSON.stringify(localStorageObject);
        localStorage.setItem(`${localStorageName}`, localObjString);
    }
};

export const getLocalStorage = (localStorageName: string) => {
    const present = Date.now();
    let localStorageObject = null;
    let retrievedLocalStorage = null;
    if (isBrowser) {
        localStorageObject = localStorage.getItem(localStorageName);
    }
    if (localStorageObject) {
        retrievedLocalStorage = JSON.parse(localStorageObject);
    }

    if (
        retrievedLocalStorage &&
        Object.hasOwn(retrievedLocalStorage, `expiry`)
    ) {
        if (present > retrievedLocalStorage?.expiry) {
            localStorage.removeItem(localStorageName);
            return null;
        }
    }

    return retrievedLocalStorage || null;
};

export const updateLocalStorage = (
    localStorageName: string,
    localStorageKey: number | string,
    localStorageVal:
        | ICampaignQueryParams
        | ICampaignQueryParams[]
        | string
        | number
) => {
    let currentLocalStorageObj = null;

    if (isBrowser) {
        currentLocalStorageObj = JSON.parse(
            localStorage.getItem(localStorageName)
        );

        if (!currentLocalStorageObj) {
            currentLocalStorageObj = {};
        }
        currentLocalStorageObj[localStorageKey] = localStorageVal;

        localStorage.setItem(
            localStorageName,
            JSON.stringify(currentLocalStorageObj)
        );
    }
};

export const isURLValid = (urlString: string) => {
    if (!isBrowser) return false;
    try {
        return Boolean(new URL(urlString));
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const queryBuilder = (filters: string[], isAppendingQuery?: boolean) => {
    if (!isBrowser) return;
    const campaignParams = new URLSearchParams();
    const queryAppend = isAppendingQuery ? `&` : `?`;
    const getCampaignTracking = getLocalStorage(`campaignTracking`);
    const storedUTMParams = getCampaignTracking?.latest_visit || null;

    if (storedUTMParams && Object.keys(storedUTMParams).length > 0) {
        for (const [key, value] of Object.entries(storedUTMParams)) {
            if (filters?.length > 0) {
                for (const filter of filters) {
                    if (key.includes(filter)) {
                        campaignParams.append(key, String(value));
                        break;
                    }
                }
            }
        }
    }

    return campaignParams.size > 0 ? `${queryAppend}${campaignParams.toString()}` : ``;
};

export const CampaignTracking = (location, termsFilter?: string[]) => {
    if (!isBrowser) return;
    const URLParams = new URLSearchParams(location.search);
    const URLParamsExist = URLParams.size > 0;
    const existingCampaignStorage = getLocalStorage(`campaignTracking`);
    let currentCampaignSession: string | null = null;
    try {
        currentCampaignSession = sessionStorage.getItem(`start_campaign_session`);
    } catch (err) {
        throw new Error(`CampaignTracking: sessionStorage unavailable - ${err}`);
    }

    const today: Date = new Date();
    const formatter = new Intl.DateTimeFormat(`en-US`, {
        year: `numeric`,
        month: `long`,
        day: `numeric`,
        hour: `2-digit`,
        minute: `2-digit`,
        second: `2-digit`,
    });
    const friendlyDate = formatter.format(today);

    const tempQueryParams: ICampaignQueryParams = {
        date: friendlyDate,
    };
    const campaignTerms = termsFilter || [];

    for (const [key, value] of URLParams) {
        if (campaignTerms.length > 0 && URLParamsExist) {
            for (const term of campaignTerms) {
                if (key.includes(term)) {
                    tempQueryParams[key.toLowerCase()] = value;
                    break;
                }
            }
        } else {
            tempQueryParams[key.toLowerCase()] = value;
        }
    }

    if (existingCampaignStorage) {
        const updateUTMParams = { ...existingCampaignStorage.latest_visit, ...tempQueryParams };
        if (currentCampaignSession) {
            if (URLParamsExist) {
                // when in the current/first visit, update existing campaignTracking storage ONLY with new UTM params - no empty parameters - handles Learn and Search
                updateLocalStorage(
                    `campaignTracking`,
                    `latest_visit`,
                    updateUTMParams
                );
            }
        } else {
            try { sessionStorage.setItem(`start_campaign_session`, friendlyDate); } catch (err) { throw new Error(`CampaignTracking: sessionStorage unavailable - ${err}`); }
            // when in returned visits/new sessions, campaignTracking storage can be empty or with UTM params, depending on the link clicked
            updateLocalStorage(
                `campaignTracking`,
                `latest_visit`,
                tempQueryParams
            );
        }
    } else {
        if (URLParamsExist) {
            try { sessionStorage.setItem(`start_campaign_session`, friendlyDate); } catch (err) { throw new Error(`CampaignTracking: sessionStorage unavailable - ${err}`); }
            localStorage.setItem(
                `campaignTracking`,
                JSON.stringify({
                    first_visit: tempQueryParams,
                    latest_visit: tempQueryParams,
                })
            );
        }
    }
};

export const generateWeeklyDates = (
    startDate: string,
    endDate: string
): string[] => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const weeks: string[] = [];

    // Get Monday of the start week
    const current = new Date(start);
    const dayOfWeek = current.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    current.setDate(current.getDate() - daysToSubtract);

    while (current <= end) {
        const year = current.getFullYear();
        const month = String(current.getMonth() + 1).padStart(2, `0`);
        const day = String(current.getDate()).padStart(2, `0`);
        const dateString = `${year}-${month}-${day}`;
        weeks.push(dateString);
        current.setDate(current.getDate() + 7);
    }

    return weeks;
};

export const buildQueryString = (
    queryParametersToString: string,
    caller: string
): string => {
    let qs = queryParametersToString || ``;
    try {
        const raw = isBrowser ? sessionStorage.getItem(`mediaCalculatorResults`) : null;
        if (raw) {
            const params = new URLSearchParams(JSON.parse(raw)).toString();
            if (params) {
                const cleanQs = qs.replace(/[?&]+$/, ``);
                const separator = cleanQs.includes(`?`) ? `&` : `?`;
                qs = `${cleanQs}${separator}${params}`;
            }
        }
    } catch (err) {
        console.error(`[${caller}] Failed to append mediaCalculatorResults to query string:`, err);
    }
    return qs;
};
